import { LinkIcon } from "@heroicons/react/outline";
import { CornerLeftUp } from "@untitled-ui/icons-react";
import { compact } from "lodash";
import { useState } from "react";
import {
  GoalArtifactSidebarFragmentFragment,
  KeyResultFragmentFragment,
} from "types/graphql-schema";

import { AlignedGoalType } from "@apps/goal-alignment/use-fetch-alignment-goals";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import { ButtonSize } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import ProgressBar from "@components/progress-bar/progress-bar";
import StartCurrentTargetTooltip from "@components/tooltip/start-current-target-tooltip";
import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";
import {
  getProgressFromStartCurrentTargetValues,
  getProgressLabel,
} from "@helpers/helpers";

import GoalKeyResultModal from "./goal-key-result-modal";

const GoalKeyResultItem = ({
  keyResult,
  goalArtifact,
  onDelete,
  isReadOnly = false,
  className = "",
}: {
  keyResult: KeyResultFragmentFragment;
  goalArtifact: GoalArtifactSidebarFragmentFragment | AlignedGoalType;
  onDelete?: (keyResult: KeyResultFragmentFragment) => void;
  isReadOnly?: boolean;
  className?: string;
}) => {
  const label = useLabel();
  const [isEditing, setIsEditing] = useState(false);

  const handleHideEditing = () => {
    setIsEditing(false);
  };

  const progress = getProgressFromStartCurrentTargetValues(keyResult);

  return (
    <>
      {isEditing && (
        <GoalKeyResultModal
          keyResult={keyResult}
          goalArtifact={goalArtifact}
          onClose={handleHideEditing}
        />
      )}
      <div className={classNames("flex gap-2 text-gray-700", className)}>
        {/* Icon is hidden in read-only (embed) contexts — the embed renders
            a single vertical line in the gutter instead. Edit contexts (the
            sidebar) keep the icon. */}
        {!isReadOnly && (
          <CornerLeftUp className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
        )}
        <div className="flex-1 text-sm flex items-start gap-1.5 [overflow-wrap:anywhere]">
          {keyResult.title}
        </div>
        {/* Keep the controls interactive above the page card's edit button. */}
        <div className="shrink-0 pointer-events-auto">
          <div className="shrink-0 flex items-center gap-2.5">
            {keyResult.kpi?.id && (
              <Tooltip
                text={`<div>Connected to KPI: ${
                  keyResult.kpi.title
                }.</div><div>The value of this ${label(
                  "key result",
                )} will be updated when the KPI's value is updated.</div>`}
              >
                <a
                  className="rounded p-0.5 -mx-0.5 hover:bg-gray-100"
                  aria-label="KR connected to KPI icon"
                  href={`/kpis/${keyResult.kpi.id}`}
                >
                  <LinkIcon className="h-4 w-4 text-gray-500" />
                </a>
              </Tooltip>
            )}
            {/* In read-only (embed) mode we show the progress as plain text
                only. Edit contexts keep the full bar. */}
            {isReadOnly ? (
              <StartCurrentTargetTooltip goalOrKeyresult={keyResult}>
                <span className="text-xs text-gray-500 tracking-tight whitespace-nowrap">
                  {getProgressLabel(keyResult, { shortVersion: true })}
                </span>
              </StartCurrentTargetTooltip>
            ) : (
              <StartCurrentTargetTooltip goalOrKeyresult={keyResult}>
                <ProgressBar
                  label={getProgressLabel(keyResult, { shortVersion: true })}
                  progress={progress}
                />
              </StartCurrentTargetTooltip>
            )}

            {/* 20x20 affordance with 16x16 avatar — matches the goal header
                and other right-side controls. */}
            <span className="flex items-center justify-center w-5 h-5 rounded-md hover:bg-black/5">
              <Avatar
                user={keyResult.assignee}
                tooltipPrefix="Assigned to "
                size="4"
              />
            </span>

            {(goalArtifact.canUpdate.permission ||
              goalArtifact.canDelete?.permission) &&
              !isReadOnly && (
                <Dropdown
                  aria-label={`${label("key result", { capitalize: true })} actions`}
                  align="end"
                  size={ButtonSize.small}
                  options={compact([
                    goalArtifact.canUpdate.permission && {
                      label: "Edit",
                      onClick: () => setIsEditing(true),
                    },
                    goalArtifact.canDelete?.permission &&
                      onDelete && {
                        label: "Delete",
                        onClick: () => onDelete(keyResult),
                      },
                  ])}
                />
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GoalKeyResultItem;
